import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ClipboardList, LifeBuoy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, tone }) => (
  <div className="min-w-[104px]">
    <p className={`text-xl font-bold tabular-nums ${tone || 'text-slate-900'}`}>{value}</p>
    <p className="text-[11px] text-slate-500">{label}</p>
  </div>
);

const TrendChart = ({ trend }) => {
  const W = 300, H = 64, PAD = 8;
  const maxResp = Math.max(20, ...trend.map((t) => t.avg_response_min || 0));
  const maxReplies = Math.max(1, ...trend.map((t) => t.replies));
  const x = (i) => PAD + (i * (W - PAD * 2)) / 6;
  const y = (v) => H - PAD - ((v / maxResp) * (H - PAD * 2));
  const line = trend.map((t, i) => (t.avg_response_min !== null ? `${x(i)},${y(t.avg_response_min)}` : null)).filter(Boolean).join(' ');
  const slaY = y(15);
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  let svg = '';
  trend.forEach((t, i) => {
    const bh = (t.replies / maxReplies) * (H - PAD * 2);
    svg += `<rect x="${x(i) - 7}" width="14" y="${H - PAD - bh}" height="${bh}" rx="2" fill="#e2e8f0"><title>${esc(t.label)}: ${t.replies} replies, ${t.dropped} dropped</title></rect>`;
  });
  svg += `<line x1="${PAD}" x2="${W - PAD}" y1="${slaY}" y2="${slaY}" stroke="#f59e0b" stroke-width="1" stroke-dasharray="3 3"/>`;
  svg += `<text x="${W - PAD + 2}" y="${slaY + 3}" font-size="8" fill="#b45309">15m</text>`;
  if (line) svg += `<polyline points="${line}" fill="none" stroke="#0d9488" stroke-width="2" stroke-linejoin="round"/>`;
  trend.forEach((t, i) => {
    if (t.avg_response_min !== null) {
      svg += `<circle cx="${x(i)}" cy="${y(t.avg_response_min)}" r="3" fill="${t.avg_response_min > 15 ? '#dc2626' : '#0d9488'}"><title>${esc(t.label)}: avg ${t.avg_response_min}m (${t.answered}/${t.replies} answered)</title></circle>`;
    }
    svg += `<text x="${x(i)}" y="${H + 10}" font-size="9" fill="${t.dropped > 0 ? '#dc2626' : '#94a3b8'}" text-anchor="middle">${esc(t.label)}</text>`;
  });
  return (
    <div data-testid="sms-scorecard-trend">
      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">7-day response time</p>
      <svg width={W} height={H + 14} className="overflow-visible" dangerouslySetInnerHTML={{ __html: svg }} />
      <p className="text-[10px] text-slate-400">Bars = replies · line = avg response · red dot = over the 15-min window · red day = dropped leads</p>
    </div>
  );
};

const AgentTable = ({ agents, days, onDays }) => (
  <div className="mt-2.5" data-testid="sms-scorecard-agents">
    <div className="flex items-center justify-between mb-1">
      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Reply speed by rep · last {days} days</p>
      <div className="flex gap-1" data-testid="sms-scorecard-window-toggle">
        {[7, 30].map((d) => (
          <button key={d} onClick={() => onDays(d)}
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${days === d ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-200 text-slate-500 hover:border-teal-400'}`}
            data-testid={`sms-scorecard-window-${d}d`}>{d}d</button>
        ))}
      </div>
    </div>
    <div className="border border-slate-100 rounded-lg overflow-hidden">
      <div className="grid grid-cols-[1fr_repeat(4,72px)] text-[10px] font-bold text-slate-400 uppercase bg-slate-50 px-3 py-1.5">
        <span>Rep</span><span className="text-right">Answered</span><span className="text-right">Avg</span><span className="text-right">Worst</span><span className="text-right">Dropped</span>
      </div>
      {agents.length === 0 && (
        <p className="text-xs text-slate-400 px-3 py-2 border-t border-slate-100" data-testid="sms-scorecard-agents-empty">
          No replies in the last {days} days — try the other window.
        </p>
      )}
      {agents.slice(0, 8).map((a) => {
        const slipping = (a.avg_response_min !== null && a.avg_response_min > 15) || a.dropped > 0;
        return (
          <div key={a.agent}
            className={`grid grid-cols-[1fr_repeat(4,72px)] text-xs px-3 py-1.5 border-t border-slate-100 ${slipping ? 'bg-red-50/60' : ''}`}
            data-testid={`sms-scorecard-agent-${a.agent}`}>
            <span className="font-semibold text-slate-800 truncate">{a.agent}{slipping && <span className="ml-1.5 text-[9px] font-bold text-red-600 uppercase">slipping</span>}</span>
            <span className="text-right text-slate-600 tabular-nums">{a.answered}</span>
            <span className={`text-right tabular-nums font-semibold ${a.avg_response_min === null ? 'text-slate-300' : a.avg_response_min > 15 ? 'text-red-600' : 'text-emerald-600'}`}>{a.avg_response_min !== null ? `${a.avg_response_min}m` : '—'}</span>
            <span className="text-right text-slate-500 tabular-nums">{a.worst_min !== null ? `${a.worst_min}m` : '—'}</span>
            <span className={`text-right tabular-nums font-semibold ${a.dropped > 0 ? 'text-red-600' : 'text-slate-400'}`}>{a.dropped}</span>
          </div>
        );
      })}
    </div>
  </div>
);

export const SmsScorecardCard = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [everActive, setEverActive] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sms/digest-scorecard?days=${days}`).then((r) => {
      setData(r.data);
      const d = r.data;
      if (d.replies > 0 || d.sent > 0 || (d.agents || []).length > 0
        || (d.trend || []).some((t) => t.replies > 0)) setEverActive(true);
    }).catch(() => {});
  }, [days]);

  if (!data || !everActive) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4" data-testid="sms-scorecard-card">
      <p className="font-semibold text-slate-900 flex items-center gap-2 mb-2.5">
        <ClipboardList className="w-5 h-5 text-teal-600" />
        Yesterday&apos;s SMS scorecard
        <span className="text-[11px] font-normal text-slate-400">{data.date} · Brisbane time</span>
      </p>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-start gap-5 flex-wrap" data-testid="sms-scorecard-stats">
          <Stat label="Replies received" value={data.replies} />
          <Stat label="Answered" value={data.answered} tone={data.answered >= data.replies ? 'text-emerald-600' : 'text-slate-900'} />
          <Stat label="Avg response" value={data.avg_response_min !== null ? `${data.avg_response_min}m` : '—'} tone={data.avg_response_min !== null && data.avg_response_min <= 15 ? 'text-emerald-600' : 'text-amber-600'} />
          <Stat label="SMS sent" value={data.sent} />
          <Stat label="Dropped leads" value={data.dropped_count} tone={data.dropped_count > 0 ? 'text-red-600' : 'text-emerald-600'} />
          {data.rescued > 0 && <Stat label="Auto-rescued" value={data.rescued} tone="text-teal-600" />}
        </div>
        {(data.trend || []).some((t) => t.replies > 0) && <TrendChart trend={data.trend} />}
      </div>
      {data.dropped_count > 0 && (
        <div className="mt-2.5 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="sms-scorecard-dropped">
          <p className="text-[11px] font-bold text-red-700 uppercase tracking-wide mb-1 flex items-center gap-1">
            <LifeBuoy className="w-3.5 h-3.5" /> Dropped leads to chase today
          </p>
          {data.dropped.map((d, i) => (
            <p key={i} className="text-xs text-slate-700 py-0.5">
              <span className="font-semibold">{d.phone}</span> — “{d.content}”
              {d.rescued && <span className="ml-1.5 text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5">queued in Revivable Deals</span>}
            </p>
          ))}
        </div>
      )}
      <AgentTable agents={data.agents || []} days={data.agent_window_days || days} onDays={setDays} />
    </div>
  );
};
